import React, { useState, useEffect, useRef } from 'react';
import { http } from '../../lib/http';

const DispatchingTab = ({ setMessage, setError }) => {
    const [dispatchData, setDispatchData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [routeFilter, setRouteFilter] = useState('all');
    const [editingRow, setEditingRow] = useState(null);
    const [editedRowData, setEditedRowData] = useState({}); // Issue #11 fix: React state instead of DOM
    const [saving, setSaving] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showCreateRouteModal, setShowCreateRouteModal] = useState(false);
    const [newRouteName, setNewRouteName] = useState('');
    const [uploadFile, setUploadFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadPreview, setUploadPreview] = useState(null);

    // PAGINATION STATE - NEW
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    // Issue #14 fix: Server-side stats for accurate counts
    const [dispatchStats, setDispatchStats] = useState({ total_customers: 0, assigned_routes: 0, paused: 0 });

    // Column visibility state
    const [visibleColumns, setVisibleColumns] = useState({
        email: true,
        contact: true,
        address: true,
        postal: true,
        itsA: true,
        deliveryInst: true,
        plan: true,
        currentPrefs: true,
        addOns: true,
        route: true,
        stopNumber: true,
        preference: true,
        avoid: true,
        pkgInst: true,
        specialInst: true,
        bags: true
    });
    const [showColumnMenu, setShowColumnMenu] = useState(false);

    // Bulk assignment state
    const [selectedCustomers, setSelectedCustomers] = useState([]);
    const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
    const [bulkRoute, setBulkRoute] = useState('');
    const [bulkStartStop, setBulkStartStop] = useState(1);
    const [bulkSearchTerm, setBulkSearchTerm] = useState('');

    // Available routes (loaded dynamically from database)
    const [availableRoutes, setAvailableRoutes] = useState([]);

    // Load available routes from database
    const loadAvailableRoutes = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await http.get('/admin/driver-management/routes', {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success && response.data.routes) {
                const routeNames = response.data.routes.map(r => r.name);
                setAvailableRoutes(routeNames);
            } else {
                // Issue #15 fix: Show error instead of stale hardcoded fallback
                setError('Failed to load routes. Please refresh the page.');
                setAvailableRoutes([]);
            }
        } catch (error) {
            // Issue #15 fix: Show error instead of stale hardcoded fallback
            setError('Failed to load routes from server. Please check your connection.');
            setAvailableRoutes([]);
        }
    };

    // Load routes on component mount
    useEffect(() => {
        loadAvailableRoutes();
    }, []);

    // Load dispatching data (customers + preferences + dispatch fields) with pagination
    const loadDispatchingData = async (page = 1, append = false, silent = false) => {
        if (append) {
            setLoadingMore(true);
        } else {
            setLoading(true);
        }

        try {
            const token = localStorage.getItem('token');
            const response = await http.get(`/admin/dispatching/data?page=${page}&limit=50&search=${searchTerm}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.dispatch_data) {
                if (append) {
                    // Append to existing data with deduplication by customer_id (Issue #10 fix)
                    setDispatchData(prev => {
                        const existingIds = new Set(prev.map(row => row.customer_id));
                        const newRows = response.data.dispatch_data.filter(
                            row => !existingIds.has(row.customer_id)
                        );
                        return [...prev, ...newRows];
                    });
                } else {
                    // Replace data
                    setDispatchData(response.data.dispatch_data);
                }

                // Update pagination state
                if (response.data.pagination) {
                    setCurrentPage(response.data.pagination.page);
                    setTotalPages(response.data.pagination.total_pages);
                    setTotalCount(response.data.pagination.total_count);
                    setHasMore(response.data.pagination.has_next);
                }

                // Issue #14 fix: Update server-side stats
                if (response.data.stats) {
                    setDispatchStats(response.data.stats);
                }

                const countMsg = append ?
                    `✅ Loaded ${response.data.dispatch_data.length} more customers (Total: ${dispatchData.length + response.data.dispatch_data.length})` :
                    `✅ Loaded ${response.data.dispatch_data.length} customers (Page ${page} of ${response.data.pagination?.total_pages || 1})`;

                if (!silent) setMessage(countMsg);
            }
        } catch (error) {
            setError(error.response?.data?.detail || 'Failed to load dispatching data');
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    // Load more customers
    const loadMoreCustomers = () => {
        if (!loadingMore && hasMore) {
            loadDispatchingData(currentPage + 1, true);
        }
    };

    // Save dispatch field updates
    const saveDispatchFields = async (customer_id, fields) => {
        setSaving(true);
        try {
            const token = localStorage.getItem('token');

            // Save all dispatch fields including stop_number in a single API call
            // The backend now handles stop_number directly (Issue #2 fix)
            await http.post('/admin/dispatching/update',
                { customer_id, ...fields },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setMessage(`✅ Updated dispatch fields for customer`);
            await loadDispatchingData(); // Reload to show updated data
            setEditingRow(null);
        } catch (error) {
            setError(error.response?.data?.message || 'Failed to update fields');
        } finally {
            setSaving(false);
        }
    };

    // Export CSV - fetches ALL customers from server (Issue #9 fix)
    const exportCSVTemplate = async () => {
        setLoading(true);
        try {
            const response = await http.get('/admin/dispatching/export-csv', {
                responseType: 'blob'
            });

            // Create download link
            const blob = new Blob([response.data]);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            // Get filename from Content-Disposition header if available
            const contentDisposition = response.headers['content-disposition'];
            let filename = `Dispatching_Export_${new Date().toISOString().split('T')[0]}.csv`;
            if (contentDisposition) {
                const match = contentDisposition.match(/filename="?(.+)"?/);
                if (match) filename = match[1];
            }

            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            setMessage(`✅ CSV exported with ALL ${totalCount} customers`);
        } catch (error) {
            setError(error.message || 'Failed to export CSV file');
        } finally {
            setLoading(false);
        }
    };

    // Export to Excel with locked columns and dropdowns
    const exportToExcel = async () => {
        setLoading(true);
        try {
            const response = await http.get('/admin/dispatching/export-excel', {
                responseType: 'blob'
            });

            // Create download link
            const blob = new Blob([response.data]);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            // Get filename from Content-Disposition header if available
            const contentDisposition = response.headers['content-disposition'];
            let filename = `Dispatching_Export_${new Date().toISOString().split('T')[0]}.xlsx`;
            if (contentDisposition) {
                const match = contentDisposition.match(/filename="?(.+)"?/);
                if (match) filename = match[1];
            }

            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            setMessage('✅ Excel file exported successfully with locked columns and dropdowns');
        } catch (error) {
            setError(error.message || 'Failed to export Excel file');
        } finally {
            setLoading(false);
        }
    };

    // Handle CSV/Excel file upload
    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const filename = file.name.toLowerCase();
        if (!filename.endsWith('.csv')) {
            setError('Please upload a CSV file. Export to Excel, edit, then save as CSV.');
            return;
        }

        setUploadFile(file);

        // RFC 4180 compliant CSV line parser - handles quoted fields with commas (Issue #7 fix)
        const parseCSVLine = (line) => {
            const result = [];
            let current = '';
            let inQuotes = false;

            for (let i = 0; i < line.length; i++) {
                const char = line[i];
                const nextChar = line[i + 1];

                if (inQuotes) {
                    if (char === '"') {
                        if (nextChar === '"') {
                            // Escaped quote ("") - add single quote and skip next
                            current += '"';
                            i++;
                        } else {
                            // End of quoted field
                            inQuotes = false;
                        }
                    } else {
                        current += char;
                    }
                } else {
                    if (char === '"') {
                        // Start of quoted field
                        inQuotes = true;
                    } else if (char === ',') {
                        // Field separator
                        result.push(current.trim());
                        current = '';
                    } else {
                        current += char;
                    }
                }
            }
            // Push last field
            result.push(current.trim());
            return result;
        };

        // Parse CSV for preview
        const reader = new FileReader();
        reader.onload = (e) => {
            const text = e.target.result;
            const lines = text.split('\n').filter(line => line.trim()); // Filter empty lines
            const headers = parseCSVLine(lines[0]);

            const preview = lines.slice(1, 6).map(line => {
                const values = parseCSVLine(line);
                return headers.reduce((obj, header, index) => {
                    obj[header] = values[index] || '';
                    return obj;
                }, {});
            });

            setUploadPreview({
                total_rows: lines.length - 1,
                preview_rows: preview
            });
        };
        reader.readAsText(file);
    };

    // Submit CSV import
    const submitCSVImport = async () => {
        if (!uploadFile) {
            setError('Please select a CSV file');
            return;
        }

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', uploadFile);

            const response = await http.post('/admin/dispatching/import', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data.success) {
                setMessage(`✅ Successfully imported ${response.data.updated_count} customer dispatch records`);
                setShowUploadModal(false);
                setUploadFile(null);
                setUploadPreview(null);
                await loadDispatchingData(1, false, true); // Silent reload
            } else {
                setError(response.data.detail || 'Failed to import CSV');
            }
        } catch (error) {
            setError('Failed to import CSV file');
        } finally {
            setUploading(false);
        }
    };

    const handleBulkAssign = async () => {
        if (!bulkRoute) {
            setError('Please select a route before bulk assigning');
            return;
        }

        if (selectedCustomers.length === 0) {
            setError('No customers selected for bulk assignment');
            return;
        }

        try {
            const token = localStorage.getItem('token');

            // Use the bulk assign endpoint - single API call instead of N calls (Issue #6 fix)
            const response = await http.post(
                '/admin/dispatch/assign-bulk',
                {
                    customer_ids: selectedCustomers,
                    route_name: bulkRoute  // Using route_name (preferred field from Issue #1 fix)
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                setMessage(`✅ ${response.data.message || `Assigned ${selectedCustomers.length} customers to ${bulkRoute}`}. Use Driver Dashboard to arrange stop order.`);
            } else {
                throw new Error(response.data.message || 'Bulk assign failed');
            }

            setShowBulkAssignModal(false);
            setSelectedCustomers([]);
            setBulkRoute('');
            await loadDispatchingData();

        } catch (error) {
            setError(error.response?.data?.message || error.message || 'Failed to assign customers to route');
        }
    };

    useEffect(() => {
        loadDispatchingData();
    }, []);

    // Debounced server-side search - Issue #5 fix
    // Triggers server reload when searchTerm changes (with 400ms debounce)
    const searchDebounceRef = useRef(null);
    const isInitialMountRef = useRef(true);
    useEffect(() => {
        // Skip initial mount (initial data load is handled by the [] useEffect above)
        if (isInitialMountRef.current) {
            isInitialMountRef.current = false;
            return;
        }

        // Clear any pending debounce
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }

        // Debounce the search to avoid hammering the server
        searchDebounceRef.current = setTimeout(() => {
            // Reset to page 1 and reload with new search term
            setCurrentPage(1);
            setSelectedCustomers([]); // Clear selection as results may change
            loadDispatchingData(1, false, true); // Silent reload to avoid spammy messages
        }, 400); // 400ms debounce

        // Cleanup on unmount or before next effect run
        return () => {
            if (searchDebounceRef.current) {
                clearTimeout(searchDebounceRef.current);
            }
        };
    }, [searchTerm]);

    // Filter data (client-side filtering for route only - search is now server-side)
    const filteredData = dispatchData.filter(row => {
        // Search filtering is now handled by the server (Issue #5 fix)
        // Only apply client-side route filter
        const matchesRoute = routeFilter === 'all' || row.route === routeFilter;
        return matchesRoute;
    });

    return (
        <div className="space-y-6">
            <div className="bg-gradient-to-r from-black to-gray-900 rounded-xl p-6 shadow-lg border-2 border-orange-500">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
                    <h2 className="text-3xl font-bold text-white mb-4 sm:mb-0 flex items-center gap-3">
                        <svg className="h-8 w-8 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                        </svg>
                        Dispatching Central Hub
                    </h2>
                    <div className="flex gap-3 flex-wrap">
                        {selectedCustomers.length > 0 && (
                            <button
                                onClick={() => setShowBulkAssignModal(true)}
                                className="px-4 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg hover:from-purple-700 hover:to-purple-800 font-semibold shadow-lg transition-all"
                            >
                                Assign {selectedCustomers.length} Stops
                            </button>
                        )}
                        <div className="relative">
                            <button
                                onClick={() => setShowColumnMenu(!showColumnMenu)}
                                className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 font-semibold shadow-lg transition-all"
                            >
                                Columns
                            </button>
                            {showColumnMenu && (
                                <div className="absolute right-0 mt-2 w-64 bg-gray-800 rounded-lg shadow-xl z-50 p-3 border border-gray-700">
                                    <p className="text-xs text-gray-300 mb-2 font-semibold">Show/Hide Columns</p>
                                    <div className="space-y-1 max-h-64 overflow-y-auto">
                                        {Object.keys(visibleColumns).map(col => (
                                            <label key={col} className="flex items-center text-sm text-gray-200 hover:bg-gray-700 p-1 rounded cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={visibleColumns[col]}
                                                    onChange={() => setVisibleColumns({ ...visibleColumns, [col]: !visibleColumns[col] })}
                                                    className="mr-2"
                                                />
                                                {col.replace(/([A-Z])/g, ' $1').trim()}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        <button
                            onClick={exportCSVTemplate}
                            disabled={loading}
                            className="px-4 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 disabled:bg-gray-600 disabled:cursor-not-allowed font-semibold shadow-lg transition-all"
                        >
                            Export CSV
                        </button>
                        <button
                            onClick={exportToExcel}
                            disabled={loading}
                            className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-lg hover:from-emerald-700 hover:to-emerald-800 disabled:bg-gray-600 disabled:cursor-not-allowed font-semibold shadow-lg transition-all"
                        >
                            Export Excel
                        </button>
                        {/* <button
                            onClick={() => setShowUploadModal(true)}
                            disabled={loading}
                            className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 disabled:bg-gray-600 disabled:cursor-not-allowed font-semibold shadow-lg transition-all"
                        >
                            Import CSV
                        </button> */}
                        <button
                            onClick={() => setShowCreateRouteModal(true)}
                            className="px-4 py-2 bg-gradient-to-r from-orange-600 to-orange-700 text-white rounded-lg hover:from-orange-700 hover:to-orange-800 font-semibold shadow-lg transition-all"
                        >
                            Create Route
                        </button>
                    </div>
                </div>

                {/* Filters and Search */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Search Customer</label>
                        <input
                            type="text"
                            placeholder="Search by name or email..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-600 rounded-md bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Filter by Route</label>
                        <select
                            value={routeFilter}
                            onChange={(e) => setRouteFilter(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-600 rounded-md bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                        >
                            <option value="all">All Routes</option>
                            {availableRoutes.map(route => (
                                <option key={route} value={route}>{route}</option>
                            ))}
                            <option value="">Unassigned</option>
                        </select>
                    </div>
                </div>

                {/* Stats - Issue #14 fix: Use server-side counts */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                    <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg p-4 text-center shadow-lg border-2 border-blue-500">
                        <p className="text-3xl font-bold text-white">{dispatchStats.total_customers}</p>
                        <p className="text-sm text-blue-100 font-medium mt-1">Total Customers</p>
                    </div>
                    <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-lg p-4 text-center shadow-lg border-2 border-green-500">
                        <p className="text-3xl font-bold text-white">
                            {dispatchStats.assigned_routes}
                        </p>
                        <p className="text-sm text-green-100 font-medium mt-1">Assigned Routes</p>
                    </div>
                    <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-lg p-4 text-center shadow-lg border-2 border-purple-500">
                        <p className="text-3xl font-bold text-white">
                            {dispatchStats.paused}
                        </p>
                        <p className="text-sm text-purple-100 font-medium mt-1">Paused</p>
                    </div>
                    <div className="bg-gradient-to-br from-orange-600 to-orange-700 rounded-lg p-4 text-center shadow-lg border-2 border-orange-500">
                        <p className="text-3xl font-bold text-white">{filteredData.length}</p>
                        <p className="text-sm text-orange-100 font-medium mt-1">Showing</p>
                    </div>
                </div>

                {/* Dispatch Table */}
                {loading ? (
                    <div className="text-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
                        <p className="mt-4 text-gray-300">Loading dispatching data...</p>
                    </div>
                ) : filteredData.length > 0 ? (
                    <div>
                        <div className="bg-gray-700 rounded-lg overflow-hidden">
                            <div className="overflow-x-auto max-h-[600px] overflow-y-auto relative">
                                <table className="min-w-full divide-y divide-gray-600">
                                    <thead className="bg-gray-600 sticky top-0 z-20">
                                        <tr>
                                            <th className="px-3 py-3 text-left sticky left-0 bg-gray-600 z-30">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedCustomers.length === filteredData.length && filteredData.length > 0}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setSelectedCustomers(filteredData.map(r => r.customer_id));
                                                        } else {
                                                            setSelectedCustomers([]);
                                                        }
                                                    }}
                                                    className="rounded"
                                                />
                                            </th>
                                            <th className="px-3 py-3 text-left text-xs font-medium text-gray-200 uppercase sticky left-12 bg-gray-600 z-30">Name</th>
                                            {visibleColumns.email && <th className="px-3 py-3 text-left text-xs font-medium text-gray-200 uppercase">Email</th>}
                                            {visibleColumns.contact && <th className="px-3 py-3 text-left text-xs font-medium text-gray-200 uppercase">Contact</th>}
                                            {visibleColumns.address && <th className="px-3 py-3 text-left text-xs font-medium text-gray-200 uppercase">Address</th>}
                                            {visibleColumns.postal && <th className="px-3 py-3 text-left text-xs font-medium text-gray-200 uppercase">Postal</th>}
                                            {visibleColumns.itsA && <th className="px-3 py-3 text-left text-xs font-medium text-gray-200 uppercase">Its A</th>}
                                            {visibleColumns.deliveryInst && <th className="px-3 py-3 text-left text-xs font-medium text-gray-200 uppercase">Delivery Inst</th>}
                                            {visibleColumns.plan && <th className="px-3 py-3 text-left text-xs font-medium text-gray-200 uppercase">Plan</th>}
                                            {visibleColumns.currentPrefs && <th className="px-3 py-3 text-left text-xs font-medium text-gray-200 uppercase">Current Prefs</th>}
                                            {visibleColumns.addOns && <th className="px-3 py-3 text-left text-xs font-medium text-gray-200 uppercase">Add-ons</th>}
                                            {visibleColumns.route && <th className="px-3 py-3 text-left text-xs font-medium text-gray-200 uppercase">Route</th>}
                                            {visibleColumns.stopNumber && <th className="px-3 py-3 text-left text-xs font-medium text-gray-200 uppercase">Stop #</th>}
                                            {visibleColumns.preference && <th className="px-3 py-3 text-left text-xs font-medium text-gray-200 uppercase">Preference</th>}
                                            {visibleColumns.avoid && <th className="px-3 py-3 text-left text-xs font-medium text-gray-200 uppercase">Avoid</th>}
                                            {visibleColumns.pkgInst && <th className="px-3 py-3 text-left text-xs font-medium text-gray-200 uppercase">Pkg Inst</th>}
                                            {visibleColumns.specialInst && <th className="px-3 py-3 text-left text-xs font-medium text-gray-200 uppercase">Special</th>}
                                            {visibleColumns.bags && <th className="px-3 py-3 text-left text-xs font-medium text-gray-200 uppercase">Bags</th>}
                                            <th className="px-3 py-3 text-left text-xs font-medium text-gray-200 uppercase">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-gray-700 divide-y divide-gray-600">
                                        {filteredData.map((row) => {
                                            const isEditing = editingRow === row.customer_id;
                                            const isPaused = row.is_paused;

                                            return (
                                                <tr
                                                    key={row.customer_id}
                                                    className={`${isPaused ? 'bg-yellow-900 bg-opacity-30' : 'hover:bg-gray-600'}`}
                                                >
                                                    <td className="px-3 py-3 sticky left-0 bg-gray-700 z-10">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedCustomers.includes(row.customer_id)}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    setSelectedCustomers([...selectedCustomers, row.customer_id]);
                                                                } else {
                                                                    setSelectedCustomers(selectedCustomers.filter(id => id !== row.customer_id));
                                                                }
                                                            }}
                                                            className="rounded"
                                                        />
                                                    </td>
                                                    <td className="px-3 py-3 text-sm text-white sticky left-12 bg-gray-700 z-10">
                                                        {isPaused && <span className="inline-block w-2 h-2 bg-yellow-500 rounded-full mr-2"></span>}
                                                        {row.first_name} {row.last_name}
                                                    </td>
                                                    {visibleColumns.email && <td className="px-3 py-3 text-sm text-gray-300">{row.email || '-'}</td>}
                                                    {visibleColumns.contact && <td className="px-3 py-3 text-sm text-gray-300">{row.contact || '-'}</td>}
                                                    {visibleColumns.address && <td className="px-3 py-3 text-sm text-gray-300">{row.address || '-'}</td>}
                                                    {visibleColumns.postal && <td className="px-3 py-3 text-sm text-gray-300">{row.postal || '-'}</td>}
                                                    {visibleColumns.itsA && <td className="px-3 py-3 text-sm text-gray-300">{row.its_a || '-'}</td>}
                                                    {visibleColumns.deliveryInst && (
                                                        <td className="px-3 py-3 text-sm text-gray-300">
                                                            <div className="max-w-xs truncate" title={row.delivery_instructions}>
                                                                {row.delivery_instructions || '-'}
                                                            </div>
                                                        </td>
                                                    )}
                                                    {visibleColumns.plan && (
                                                        <td className="px-3 py-3 text-sm text-gray-300">
                                                            <div className="max-w-xs truncate" title={row.plan}>{row.plan || '-'}</div>
                                                        </td>
                                                    )}
                                                    {visibleColumns.currentPrefs && (
                                                        <td className="px-3 py-3 text-sm text-gray-300">
                                                            <div className="text-xs text-gray-400">{row.current_preferences || '-'}</div>
                                                        </td>
                                                    )}
                                                    {visibleColumns.addOns && (
                                                        <td className="px-3 py-3 text-sm">
                                                            <span className="text-gray-300 text-xs">{row.add_ons || '-'}</span>
                                                        </td>
                                                    )}
                                                    {visibleColumns.route && (
                                                        <td className="px-3 py-3 text-sm">
                                                            {isEditing ? (
                                                                <select
                                                                    value={editedRowData.route || ''}
                                                                    onChange={(e) => setEditedRowData(prev => ({ ...prev, route: e.target.value }))}
                                                                    className="px-2 py-1 bg-gray-600 text-white border border-gray-500 rounded text-xs w-full"
                                                                >
                                                                    <option value="">Unassigned</option>
                                                                    {availableRoutes.map(r => <option key={r} value={r}>{r}</option>)}
                                                                </select>
                                                            ) : (
                                                                <span className={`px-2 py-1 rounded text-xs ${row.route ? 'bg-blue-600 text-white' : 'bg-gray-600 text-gray-300'}`}>
                                                                    {row.route || 'Unassigned'}
                                                                </span>
                                                            )}
                                                        </td>
                                                    )}
                                                    {visibleColumns.stopNumber && (
                                                        <td className="px-3 py-3 text-sm">
                                                            {isEditing ? (
                                                                <input
                                                                    type="number"
                                                                    value={editedRowData.stop_number || ''}
                                                                    onChange={(e) => setEditedRowData(prev => ({ ...prev, stop_number: e.target.value }))}
                                                                    className="px-2 py-1 bg-gray-600 text-white border border-gray-500 rounded text-xs w-16"
                                                                    placeholder="#"
                                                                    min="0"
                                                                />
                                                            ) : (
                                                                <span className="text-gray-300 font-semibold">{row.stop_number || '-'}</span>
                                                            )}
                                                        </td>
                                                    )}
                                                    {visibleColumns.preference && (
                                                        <td className="px-3 py-3 text-sm">
                                                            {isEditing ? (
                                                                <input
                                                                    type="text"
                                                                    value={editedRowData.preference || ''}
                                                                    onChange={(e) => setEditedRowData(prev => ({ ...prev, preference: e.target.value }))}
                                                                    className="px-2 py-1 bg-gray-600 text-white border border-gray-500 rounded text-xs w-full"
                                                                    placeholder="e.g. L(Veg), D(Chicken)"
                                                                />
                                                            ) : (
                                                                <span className="text-gray-300 text-xs">{row.preference || '-'}</span>
                                                            )}
                                                        </td>
                                                    )}
                                                    {visibleColumns.avoid && (
                                                        <td className="px-3 py-3 text-sm">
                                                            {isEditing ? (
                                                                <input
                                                                    type="text"
                                                                    value={editedRowData.avoid || ''}
                                                                    onChange={(e) => setEditedRowData(prev => ({ ...prev, avoid: e.target.value }))}
                                                                    className="px-2 py-1 bg-gray-600 text-white border border-gray-500 rounded text-xs w-full"
                                                                    placeholder="e.g. No onions"
                                                                />
                                                            ) : (
                                                                <span className="text-gray-300 text-xs">{row.avoid || '-'}</span>
                                                            )}
                                                        </td>
                                                    )}
                                                    {visibleColumns.pkgInst && (
                                                        <td className="px-3 py-3 text-sm">
                                                            {isEditing ? (
                                                                <input
                                                                    type="text"
                                                                    value={editedRowData.packaging_instruction || ''}
                                                                    onChange={(e) => setEditedRowData(prev => ({ ...prev, packaging_instruction: e.target.value }))}
                                                                    className="px-2 py-1 bg-gray-600 text-white border border-gray-500 rounded text-xs w-full"
                                                                    placeholder="e.g. Extra bag"
                                                                />
                                                            ) : (
                                                                <span className="text-gray-300 text-xs">{row.packaging_instruction || '-'}</span>
                                                            )}
                                                        </td>
                                                    )}
                                                    {visibleColumns.specialInst && (
                                                        <td className="px-3 py-3 text-sm">
                                                            {isEditing ? (
                                                                <input
                                                                    type="text"
                                                                    value={editedRowData.special_instruction || ''}
                                                                    onChange={(e) => setEditedRowData(prev => ({ ...prev, special_instruction: e.target.value }))}
                                                                    className="px-2 py-1 bg-gray-600 text-white border border-gray-500 rounded text-xs w-full"
                                                                    placeholder="e.g. Extra rice"
                                                                />
                                                            ) : (
                                                                <span className="text-gray-300 text-xs">{row.special_instruction || '-'}</span>
                                                            )}
                                                        </td>
                                                    )}
                                                    {visibleColumns.bags && (
                                                        <td className="px-3 py-3 text-sm">
                                                            {isEditing ? (
                                                                <input
                                                                    type="number"
                                                                    value={editedRowData.bag_count || ''}
                                                                    onChange={(e) => setEditedRowData(prev => ({ ...prev, bag_count: e.target.value }))}
                                                                    className="px-2 py-1 bg-gray-600 text-white border border-gray-500 rounded text-xs w-16"
                                                                    placeholder="3"
                                                                    min="0"
                                                                />
                                                            ) : (
                                                                <span className="text-gray-300 font-semibold">{row.bag_count || '-'}</span>
                                                            )}
                                                        </td>
                                                    )}
                                                    <td className="px-3 py-3 text-sm">
                                                        {isEditing ? (
                                                            <div className="flex gap-1">
                                                                <button
                                                                    onClick={() => {
                                                                        // Issue #11 fix: Read from React state instead of DOM
                                                                        const fields = {
                                                                            route: editedRowData.route || '',
                                                                            stop_number: parseInt(editedRowData.stop_number) || null,
                                                                            preference: editedRowData.preference || '',
                                                                            avoid: editedRowData.avoid || '',
                                                                            packaging_instruction: editedRowData.packaging_instruction || '',
                                                                            special_instruction: editedRowData.special_instruction || '',
                                                                            bag_count: parseInt(editedRowData.bag_count) || 0
                                                                        };
                                                                        saveDispatchFields(row.customer_id, fields);
                                                                    }}
                                                                    disabled={saving}
                                                                    className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 disabled:opacity-50"
                                                                >
                                                                    ✓
                                                                </button>
                                                                <button
                                                                    onClick={() => {
                                                                        setEditingRow(null);
                                                                        setEditedRowData({});
                                                                    }}
                                                                    disabled={saving}
                                                                    className="px-2 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-700 disabled:opacity-50"
                                                                >
                                                                    ✕
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <button
                                                                onClick={() => {
                                                                    // Initialize edited data with current row values
                                                                    setEditedRowData({
                                                                        route: row.route || '',
                                                                        stop_number: row.stop_number || '',
                                                                        preference: row.preference || '',
                                                                        avoid: row.avoid || '',
                                                                        packaging_instruction: row.packaging_instruction || '',
                                                                        special_instruction: row.special_instruction || '',
                                                                        bag_count: row.bag_count || ''
                                                                    });
                                                                    setEditingRow(row.customer_id);
                                                                }}
                                                                className="px-2 py-1 bg-orange-600 text-white rounded text-xs hover:bg-orange-700"
                                                            >
                                                                Edit
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* LOAD MORE BUTTON - NEW */}
                        {hasMore && !loading && (
                            <div className="mt-4 text-center">
                                <button
                                    onClick={loadMoreCustomers}
                                    disabled={loadingMore}
                                    className="px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-700 font-semibold shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {loadingMore ? (
                                        <span className="flex items-center gap-2">
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                            Loading more...
                                        </span>
                                    ) : (
                                        `Load More (Showing ${dispatchData.length} of ${totalCount})`
                                    )}
                                </button>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="text-center py-12 bg-gray-700 rounded-lg">
                        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                        </svg>
                        <p className="mt-4 text-gray-300 text-lg font-medium">No customers found</p>
                        <p className="mt-2 text-gray-400 text-sm">
                            {searchTerm || routeFilter !== 'all' ? 'Try adjusting your filters' : 'Customer data will appear here'}
                        </p>
                    </div>
                )}
            </div>

            {/* Bulk Assignment Modal */}
            {showBulkAssignModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-gradient-to-br from-gray-900 to-black rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto border-2 border-orange-500 shadow-2xl">
                        <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                            <svg className="h-6 w-6 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            Bulk Assign to Route ({selectedCustomers.length} customers)
                        </h3>

                        {/* Route Selection */}
                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-gray-300 mb-2">
                                Select Route
                            </label>
                            <select
                                value={bulkRoute}
                                onChange={(e) => setBulkRoute(e.target.value)}
                                className="w-full px-3 py-2 bg-gray-700 border-2 border-gray-600 rounded-lg text-white focus:border-orange-500 focus:outline-none"
                            >
                                <option value="">Choose route...</option>
                                {availableRoutes.map(route => (
                                    <option key={route} value={route}>{route}</option>
                                ))}
                            </select>
                            <p className="text-xs text-gray-400 mt-2">
                                Note: Stop numbers will be assigned in Driver Dashboard via drag-and-drop
                            </p>
                        </div>

                        {/* Customer Search */}
                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-gray-300 mb-2">
                                Search Selected Customers
                            </label>
                            <input
                                type="text"
                                value={bulkSearchTerm}
                                onChange={(e) => setBulkSearchTerm(e.target.value)}
                                placeholder="Filter by name..."
                                className="w-full px-3 py-2 bg-gray-700 border-2 border-gray-600 rounded-lg text-white focus:border-orange-500 focus:outline-none"
                            />
                        </div>

                        {/* Selected Customers List */}
                        <div className="mb-4 max-h-64 overflow-y-auto bg-gray-800 rounded-lg p-3 border border-gray-700">
                            <p className="text-sm font-semibold text-orange-500 mb-2">Selected Customers:</p>
                            {dispatchData
                                .filter(c => selectedCustomers.includes(c.customer_id))
                                .filter(c => !bulkSearchTerm ||
                                    `${c.first_name} ${c.last_name}`.toLowerCase().includes(bulkSearchTerm.toLowerCase())
                                )
                                .map((customer, idx) => (
                                    <div key={customer.customer_id} className="flex items-center justify-between py-2 border-b border-gray-700">
                                        <span className="text-white font-medium">
                                            {customer.first_name} {customer.last_name}
                                        </span>
                                        <button
                                            onClick={() => setSelectedCustomers(selectedCustomers.filter(id => id !== customer.customer_id))}
                                            className="text-red-400 hover:text-red-300 text-sm font-semibold transition-colors"
                                        >
                                            Remove
                                        </button>
                                    </div>
                                ))}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-3">
                            <button
                                onClick={handleBulkAssign}
                                disabled={!bulkRoute}
                                className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 disabled:bg-gray-600 text-white rounded-lg font-semibold shadow-lg transition-all"
                            >
                                Assign to {bulkRoute || 'Route'}
                            </button>
                            <button
                                onClick={() => {
                                    setShowBulkAssignModal(false);
                                    setBulkSearchTerm('');
                                }}
                                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-semibold shadow-lg transition-all"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Create Route Modal */}
            {showCreateRouteModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-gradient-to-br from-gray-900 to-black rounded-xl p-6 w-full max-w-md border-2 border-orange-500 shadow-2xl">
                        <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                            <svg className="h-6 w-6 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            Create New Route
                        </h3>

                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Route Name
                            </label>
                            <input
                                type="text"
                                value={newRouteName}
                                onChange={(e) => setNewRouteName(e.target.value)}
                                placeholder="e.g., Scarborough, North York..."
                                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                            />
                            <p className="text-xs text-gray-400 mt-2">
                                💡 This route will be available in Driver Management for assignment
                            </p>
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={async () => {
                                    if (!newRouteName.trim()) {
                                        setError('Please enter a route name');
                                        return;
                                    }

                                    try {
                                        const token = localStorage.getItem('token');
                                        // Correct endpoint: /admin/logistics/routes
                                        const response = await http.post('/admin/logistics/routes',
                                            { name: newRouteName.trim() },
                                            { headers: { Authorization: `Bearer ${token}` } }
                                        );

                                        if (response.data.success) {
                                            setMessage(`✅ Route "${newRouteName}" created successfully!`);
                                            setNewRouteName('');
                                            setShowCreateRouteModal(false);

                                            // Reload routes to show new route in dropdowns
                                            await loadAvailableRoutes();
                                            // Reload dispatching data
                                            await loadDispatchingData();
                                        }
                                    } catch (error) {
                                        setError(error.response?.data?.detail || 'Failed to create route');
                                    }
                                }}
                                disabled={!newRouteName.trim()}
                                className="flex-1 px-4 py-2 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-600 text-white rounded font-medium"
                            >
                                Create Route
                            </button>
                            <button
                                onClick={() => {
                                    setShowCreateRouteModal(false);
                                    setNewRouteName('');
                                }}
                                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Upload CSV Modal */}
            {showUploadModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-gray-800 rounded-lg p-6 w-full max-w-2xl">
                        <h3 className="text-xl font-bold text-white mb-4">📤 Import Dispatch Data</h3>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-300 mb-2">Select CSV File</label>
                            <input
                                type="file"
                                accept=".csv"
                                onChange={handleFileUpload}
                                className="block w-full text-sm text-gray-300 border border-gray-600 rounded-lg cursor-pointer bg-gray-700 focus:outline-none p-2"
                            />
                            <p className="text-xs text-gray-400 mt-2">
                                💡 Export to Excel, edit in Excel, then save as CSV and import here
                            </p>
                        </div>

                        {uploadPreview && (
                            <div className="mb-4 p-3 bg-gray-700 rounded">
                                <p className="text-sm text-gray-300">
                                    📊 Preview: {uploadPreview.total_rows} rows detected
                                </p>
                            </div>
                        )}

                        <div className="flex gap-3">
                            <button
                                onClick={submitCSVImport}
                                disabled={!uploadFile || uploading}
                                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white rounded font-medium"
                            >
                                {uploading ? 'Importing...' : 'Import Data'}
                            </button>
                            <button
                                onClick={() => {
                                    setShowUploadModal(false);
                                    setUploadFile(null);
                                    setUploadPreview(null);
                                }}
                                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DispatchingTab;
